import type {
  GrowthAutopilotAction,
  GrowthBrainInsight,
  GrowthDashboardData,
  GrowthIconName,
  GrowthMetricDef,
  GrowthQuickNavItem,
  GrowthToneKey,
} from '@elva/ui';
import { growthGet } from '@/data/http';
import type { BeDashboard } from '@/data/marketingApiTypes';
import { deepCamel, num, relativeTimeFromIso, str } from '@/data/adapt';

interface BeDashboardTile {
  key?: string;
  label?: string;
  value?: number | string;
  value_cents?: number;
}

interface BePmsPoweredMoves {
  narrative?: string;
  month?: string | null;
}

interface BeRecentAction {
  time?: string;
  timestamp?: string;
  icon?: string;
  cat?: string;
  category?: string;
  tone?: string;
  action?: string;
  detail?: string;
  entity?: string;
}

const GROWTH_ICONS = new Set<GrowthIconName>([
  'dollar',
  'alert',
  'bolt',
  'check',
  'clock',
  'swap',
  'minus',
  'target',
  'store',
  'search',
  'globe',
  'doc',
]);

const GROWTH_TONES = new Set<GrowthToneKey>(['ai', 'info', 'success', 'gold', 'terra', 'lav']);

function formatPeriod(month: string | null | undefined, asOf: string | null | undefined): string {
  if (month) {
    if (/^\d{4}-\d{2}$/.test(month)) {
      const [y, m] = month.split('-');
      const date = new Date(Number(y), Number(m) - 1, 1);
      if (Number.isFinite(date.getTime())) {
        const label = date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
        return `${label} · month-to-date`;
      }
    }
    return month.includes('month-to-date') ? month : `${month} · month-to-date`;
  }
  if (asOf) {
    const date = new Date(asOf);
    if (Number.isFinite(date.getTime())) {
      const label = date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
      return `${label} · month-to-date`;
    }
  }
  return 'Month-to-date';
}

function formatUpdated(
  asOf: string | null | undefined,
  freshness: string | null | undefined,
  staleAsOf: string | null | undefined,
): string {
  const base = asOf ? `updated ${relativeTimeFromIso(asOf)}` : 'updated recently';
  if (freshness === 'degraded') {
    const stale = staleAsOf ? ` · stale ${relativeTimeFromIso(staleAsOf)}` : '';
    return `${base} · degraded data${stale}`;
  }
  if (freshness && freshness !== 'fresh') return `${base} · ${freshness.replace(/_/g, ' ')}`;
  return base;
}

function formatCentsValue(cents: number): string {
  return Math.round(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function adaptTile(tile: BeDashboardTile, degraded: boolean, isFirst: boolean): GrowthMetricDef {
  const key = (tile.key ?? '').toLowerCase();
  const label = str(tile.label, '');
  const metric: GrowthMetricDef = { label, value: '0' };

  if (tile.value_cents != null) {
    metric.prefix = '$';
    metric.value = formatCentsValue(num(tile.value_cents));
  } else if (key === 'roi_percent') {
    metric.value = tile.value == null ? '—' : `${num(tile.value)}%`;
    if (num(tile.value) >= 100) metric.accent = 'success';
  } else {
    metric.value = str(tile.value);
  }

  if (degraded && isFirst) {
    metric.sub = 'Data freshness degraded';
    metric.accent = 'gold';
  }

  return metric;
}

function adaptIcon(raw: string | undefined): GrowthIconName {
  const key = (raw ?? 'bolt').toLowerCase() as GrowthIconName;
  return GROWTH_ICONS.has(key) ? key : 'bolt';
}

function adaptTone(raw: string | undefined): GrowthToneKey {
  const key = (raw ?? 'ai').toLowerCase() as GrowthToneKey;
  return GROWTH_TONES.has(key) ? key : 'ai';
}

function adaptRecentAction(row: BeRecentAction): GrowthAutopilotAction {
  const c = deepCamel<Record<string, unknown>>(row);
  const timestamp = str(c.timestamp ?? c.time, '');
  const time =
    c.time && !String(c.time).includes('T') ? str(c.time) : timestamp ? relativeTimeFromIso(timestamp) : '—';
  const detail = str(c.action ?? c.detail, '');
  const entity = str(c.entity, '');
  const action = entity && detail ? `${entity} — ${detail}` : detail || entity || '—';

  return {
    time,
    icon: adaptIcon(str(c.icon, 'bolt')),
    cat: str(c.cat ?? c.category, 'Autopilot'),
    tone: adaptTone(str(c.tone, 'ai')),
    action,
  };
}

function adaptBrainInsights(moves: BePmsPoweredMoves | undefined): GrowthBrainInsight[] {
  const narrative = (moves?.narrative ?? '').trim();
  if (!narrative) return [];
  return [{ heading: 'PMS-powered moves', body: narrative }];
}

function adaptHighlight(raw: Record<string, unknown> | undefined) {
  if (!raw) return undefined;
  const c = deepCamel<Record<string, unknown>>(raw);
  const stats = (c.stats as Array<[string, string, string]> | undefined) ?? [];
  const name = str(c.name ?? c.keyword);

  if (!stats.length && (c.bookings != null || c.revenue != null || c.cpa != null)) {
    const bookings = str(c.bookings);
    const revenue = c.revenue != null ? `$${num(c.revenue).toLocaleString()}` : '—';
    const cpa = c.cpa != null ? `$${Math.round(num(c.cpa) / 100).toLocaleString()}` : '—';
    return {
      name,
      subtitle: `${bookings} bookings · ${revenue} est. revenue · ${cpa} CPA`,
      stats: [
        ['CTR', c.ctr != null ? `${num(c.ctr)}%` : '—', 'var(--success)'],
        ['Conversion', c.conversion != null ? `${num(c.conversion)}%` : '—', 'var(--success)'],
        [
          'Spend',
          c.spend != null ? `$${Math.round(num(c.spend) / 100).toLocaleString()}` : '—',
          'var(--ink-0)',
        ],
      ] as [string, string, string][],
    };
  }

  if (!stats.length && (c.from != null || c.to != null || c.monthlySearches != null)) {
    return {
      name,
      subtitle: `${str(c.from)} → ${str(c.to)} in ${str(c.days)} days`,
      stats: [
        ['Monthly searches', str(c.monthlySearches), 'var(--ink-0)'],
        ['Est. traffic gain', str(c.trafficGain), 'var(--success)'],
      ] as [string, string, string][],
    };
  }

  return {
    name,
    subtitle: str(c.subtitle ?? c.sub),
    stats: stats as readonly [string, string, string][],
  };
}

function adaptQuickNav(rows: Array<Record<string, unknown>>): GrowthQuickNavItem[] {
  return rows.map((row) => {
    const c = deepCamel<Record<string, unknown>>(row);
    return {
      title: str(c.title),
      sub: str(c.sub ?? c.subtitle),
      icon: adaptIcon(str(c.icon, 'bolt')),
      tone: adaptTone(str(c.tone, 'ai')),
    };
  });
}

function adaptEnrichedMetric(row: Record<string, unknown>): GrowthMetricDef {
  const c = deepCamel<Record<string, unknown>>(row);
  const metric: GrowthMetricDef = {
    label: str(c.label),
    value: str(c.value),
  };
  if (c.prefix != null) metric.prefix = str(c.prefix);
  if (c.delta != null) metric.delta = str(c.delta);
  if (c.deltaDir != null) metric.deltaDir = c.deltaDir as NonNullable<GrowthMetricDef['deltaDir']>;
  if (c.deltaLabel != null) metric.deltaLabel = str(c.deltaLabel);
  if (c.sub != null) metric.sub = str(c.sub);
  if (c.accent != null) metric.accent = c.accent as NonNullable<GrowthMetricDef['accent']>;
  if (c.bar != null && Number.isFinite(Number(c.bar))) metric.bar = num(c.bar);
  return metric;
}

function adaptStubDashboard(be: BeDashboard): GrowthDashboardData {
  const degraded = be.data_freshness === 'degraded';
  const brainInsights = adaptBrainInsights(be.pms_powered_moves);
  const recentActions = (be.recent_actions ?? []).map(adaptRecentAction);

  return {
    period: formatPeriod(be.pms_powered_moves?.month, be.as_of),
    updated: formatUpdated(be.as_of, be.data_freshness, be.stale_as_of),
    approvalsTotal: 0,
    approvalBreakdown: [],
    businessMetrics: (be.tiles ?? []).map((tile, index) => adaptTile(tile, degraded, index === 0)),
    brainInsightCount: brainInsights.length || recentActions.length,
    brainInsights,
    autopilotActions: recentActions,
  };
}

function adaptEnrichedDashboard(be: BeDashboard): GrowthDashboardData {
  const breakdown =
    be.approvals?.breakdown?.map(
      (b) => [String(b.count ?? 0), str(b.label, '')] as const,
    ) ?? [];

  const topCampaign = adaptHighlight(be.highlights?.top_campaign);
  const biggestRankingGain = adaptHighlight(be.highlights?.biggest_ranking_gain);

  const autopilotActions =
    be.autopilot_actions?.map((a) => adaptRecentAction(deepCamel<BeRecentAction>(a))) ?? [];

  const brainInsights =
    be.brain_insight?.items?.map((i) => ({
      heading: str(i.heading),
      body: str(i.body),
    })) ?? [];

  return {
    period: be.meta?.period ?? formatPeriod(undefined, be.as_of),
    updated: be.meta?.updated_at
      ? `updated ${relativeTimeFromIso(be.meta.updated_at)}`
      : formatUpdated(be.as_of, be.data_freshness, be.stale_as_of),
    ...(be.meta?.autopilot_active != null || be.autopilot_active != null
      ? { autopilotActive: be.meta?.autopilot_active ?? be.autopilot_active === true }
      : {}),
    approvalsTotal: be.approvals?.total ?? num(breakdown.reduce((a, [c]) => a + Number(c), 0)),
    approvalBreakdown: breakdown,
    businessMetrics: be.business_metrics?.map(adaptEnrichedMetric) ?? [],
    brainInsightCount: be.brain_insight?.count ?? brainInsights.length,
    brainInsights,
    ...(topCampaign ? { topCampaign } : {}),
    ...(biggestRankingGain ? { biggestRankingGain } : {}),
    autopilotActions,
    ...(be.quick_nav ? { quickNav: adaptQuickNav(be.quick_nav) } : {}),
  };
}

export function adaptDashboard(be: BeDashboard): GrowthDashboardData {
  if (be.tiles?.length) return adaptStubDashboard(be);
  return adaptEnrichedDashboard(be);
}

export async function fetchDashboard(): Promise<GrowthDashboardData> {
  const be = await growthGet<BeDashboard>('/dashboard/');
  return adaptDashboard(be);
}
